import { AssemblyAI } from "assemblyai";
import {inject, Injectable} from '@angular/core';
import { environment as llm_env } from '@env/environments.llm-dev';
import { Observable, from } from 'rxjs';
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";

/**
 * @Injectable({ providedIn: 'root' })
 * Este decorador marca la clase como un servicio que puede ser inyectado en otros componentes o servicios.
 * `providedIn: 'root'` significa que Angular creará una única instancia de este servicio (singleton)
 * y estará disponible en toda la aplicación.
 */
@Injectable({ providedIn: 'root' })
export class llm_assembly_trascription_service {
  //Injectables
  private _console = inject(Tools_aiConsoleService);
  /**
   * Cliente de AssemblyAI.
   * Se inicializa con la clave API obtenida de las variables de entorno.
   */
  private client = new AssemblyAI({
    apiKey: llm_env.ASSEMBLYAI_API_KEY,
  });

  /**
   * Transcribe un archivo de audio a texto.
   *
   * @param audioBlob El archivo de audio (en formato Blob) que se va a transcribir.
   * @returns Un Observable que emite el texto transcrito como una cadena.
   * Si la transcripción falla o no se reconoce texto, se maneja adecuadamente.
   */
  transcriptAudio(audioBlob: Blob): Observable<any> {
    return from(this.transcribeAudioAsync(audioBlob));
  }

  /**
   * Método asíncrono privado para manejar la lógica de transcripción de audio.
   *
   * @param audioBlob El archivo de audio (Blob) a transcribir.
   * @returns Una Promesa que se resuelve con el texto transcrito.
   * @throws Error si el formato de audio no es compatible o si ocurre un error durante la transcripción.
   */
  private async transcribeAudioAsync(audioBlob: Blob): Promise<any> {
    try {
      this._console.log('audioBlob', audioBlob);


        // --- INICIO: CÓDIGO PARA PRUEBAS ---
        // Llama a la nueva función para descargar el Blob antes de enviarlo.
        // Puedes comentar o eliminar esta línea cuando ya no necesites depurar.
        //this.downloadAudioForTesting(audioBlob, `audio-test-${Date.now()}.wav`);
        // --- FIN: CÓDIGO PARA PRUEBAS ---


      // Verificar formato usando expresión regular
      const isSupportedFormat = /^(audio\/(wav|mp3|webm|flac))|(audio\/webm;codecs=opus)$/.test(audioBlob.type);
      if (!audioBlob.type || !isSupportedFormat) {
        throw new Error(`Formato de audio no compatible: ${audioBlob.type}. Usa WAV, MP3, WebM (con/sin codecs) o FLAC.`);
      }

      const audioFile = new File([audioBlob], 'audio-recording', {
        type: audioBlob.type,
        lastModified: Date.now()
      });


      const transcript = await this.client.transcripts.transcribe({
        audio: audioFile,
        language_code: "pt",
        speech_model: "universal",
        entity_detection: false
      });

      return ({"text":`${transcript.text || '[Não foi reconhecido texto]'}`, "entities": `${transcript.entities}`});
    } catch (error) {
      this._console.error('Error en transcripción:', error);
      throw new Error('Fallo en la transcripción: ' + (error as Error).message);
    }
  }



    /**
     * Descarga un Blob de audio al PC del usuario para pruebas locales.
     *
     * @param audioBlob El archivo de audio (Blob) que se va a descargar.
     * @param fileName El nombre que tendrá el archivo (ej. 'test-audio.wav').
     */
    downloadAudioForTesting(audioBlob: Blob, fileName: string): void {
        try {
            // 1. Crea una URL local en el navegador para el objeto Blob.
            const url = window.URL.createObjectURL(audioBlob);

            // 2. Crea un elemento de anclaje (<a>) temporal en la memoria.
            const a = document.createElement('a');
            document.body.appendChild(a); // Es necesario añadirlo al DOM para que funcione en Firefox.
            a.style.display = 'none';

            // 3. Asigna la URL del Blob al enlace y establece el atributo de descarga.
            a.href = url;
            a.download = fileName;

            // 4. Simula un clic en el enlace para iniciar la descarga del archivo.
            a.click();

            // 5. Limpia la URL creada para liberar memoria.
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a); // Remueve el elemento del DOM.

            this._console.log(`Descarga de "${fileName}" iniciada correctamente.`);

        } catch (error) {
            this._console.error('Error al intentar descargar el audio:', error);
        }
    }
}
